import { useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { AppShell } from '@/components/px/AppShell'
import { ConceptBadge } from '@/components/px/ConceptBadge'
import { ConfidenceBar } from '@/components/px/ConfidenceBar'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill, type StatusTone } from '@/components/px/StatusPill'
import { projectNav } from '@/components/px/nav'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  MAPPING_TABLE,
  PROPOSED_FIELDS,
  SPLIT,
  SPLIT_RECORD,
  mappingRows,
  type MappingStatus
} from '@/demo/content/migration'
import { selectActiveOrgId } from '@/features/auth/slices/sessionSlice'
import { ProjectBlockedState } from '@/features/projects/components/ProjectBlockedState'
import {
  closeProject,
  openProject,
  selectProjectDetail
} from '@/features/projects/slices/projectDetailSlice'
import { GoNoGoPanel } from '@/features/migration/components/GoNoGoPanel'
import { MigrationPlansPanel } from '@/features/migration/components/MigrationPlansPanel'
import { MigrationRunsPanel } from '@/features/migration/components/MigrationRunsPanel'
import { MigrationTrailPanel } from '@/features/migration/components/MigrationTrailPanel'
import { ReconciliationPanel } from '@/features/migration/components/ReconciliationPanel'
import { RollbackPanel } from '@/features/migration/components/RollbackPanel'
import {
  migrationClosed,
  openMigration,
  selectMigrationBlocked,
  selectSelectedPlanId,
  selectSelectedRunId
} from '@/features/migration/slices/migrationSlice'
import { cn } from '@/lib/utils'
import { useAppDispatch, useAppSelector } from '@/store'

const MAPPING_TONE: Record<MappingStatus, StatusTone> = {
  Mapped: 'done',
  'Needs a person': 'review',
  'Blocked · client': 'blocked'
}

const rowWash = (status: MappingStatus): string | undefined => {
  if (status === 'Blocked · client') return 'bg-blocked-bg font-semibold'
  if (status === 'Needs a person') return 'bg-review-bg font-medium'
  return undefined
}

/**
 * The ONE badged field-mapping section (Agon's 2026-09-18 ruling). Renders
 * the fixture's column-level mapping table, the ambiguous-column proposal
 * and its record, all from `@/demo/content/migration`. Issues NO network
 * call and offers NO act — the fixture's single split-decision button does
 * not survive: `mappingRows` and `SPLIT_RECORD`'s decision half are always
 * rendered in their PENDING state, since without the retired `demoSlice`
 * there is no decision to reflect.
 *
 * The `@/demo/*` import lives HERE, in the page module, matching the
 * established `conceptScope: 'section'` precedent (`ClientPortalPage.tsx`,
 * `MyBoardPage.tsx`) rather than a delegate child component:
 * `demoBoundary.test.ts`'s allow list is derived from `conceptPageModules()`
 * (ALL concept rows, page AND section), so the registry's `pageModule` is
 * the file the boundary test expects to hold the import, and its
 * "allow-listed but demo-free" check fails a page that delegates the whole
 * import away. INV-5.8 / the spec's own wording confirms this page is the
 * one exception to "no module under `features/migration/` imports `@/demo/`".
 */
function FieldMappingConceptSection() {
  const { t } = useTranslation('projectWorkspace')
  const rows = mappingRows(false)

  return (
    <div data-testid="field-mapping-concept-content">
      <p className="mt-1.5 text-sm text-ink-soft">{t('migration.demoBand.note')}</p>

      <div className="mt-4 rounded-lg border border-border bg-card">
        <div className="border-b border-border px-4 py-3">
          <SectionLabel>{MAPPING_TABLE.label}</SectionLabel>
          <h3 className="mt-0.5 font-serif text-base font-semibold text-foreground">{MAPPING_TABLE.heading}</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">{MAPPING_TABLE.caption}</p>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{MAPPING_TABLE.columns.legacyField}</TableHead>
              <TableHead>{MAPPING_TABLE.columns.odooTarget}</TableHead>
              <TableHead>{MAPPING_TABLE.columns.rule}</TableHead>
              <TableHead>{MAPPING_TABLE.columns.confidence}</TableHead>
              <TableHead>{MAPPING_TABLE.columns.status}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.key} className={cn(rowWash(row.status))}>
                <TableCell className="px-4 font-mono text-[13px] text-foreground">{row.legacyField}</TableCell>
                <TableCell>
                  {row.targetNote ? (
                    <span className="text-[13px] text-foreground">{row.targetNote}</span>
                  ) : row.targetModel && row.targetField ? (
                    <span className="flex flex-col leading-tight">
                      <span className="font-mono text-[13px] text-foreground">{row.targetModel}</span>
                      <span className="font-mono text-xs text-muted-foreground">{row.targetField}</span>
                    </span>
                  ) : (
                    <span className="text-muted-foreground" aria-label="no target yet">
                      —
                    </span>
                  )}
                </TableCell>
                <TableCell className="whitespace-normal text-[13px]">{row.rule}</TableCell>
                <TableCell>
                  <ConfidenceBar pct={row.confidencePct} />
                </TableCell>
                <TableCell className="px-4">
                  <StatusPill tone={MAPPING_TONE[row.status]}>{row.status}</StatusPill>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="mt-4 rounded-lg border border-border bg-accent px-4 py-4">
        <div className="flex flex-wrap items-center gap-2">
          <SectionLabel>{SPLIT.panelLabel}</SectionLabel>
          <StatusPill tone="proposed">{SPLIT.proposalTag}</StatusPill>
        </div>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground">{SPLIT.prose}</p>
      </div>

      <div className="mt-4 rounded-lg border border-border bg-card px-4 py-4">
        <SectionLabel>{SPLIT_RECORD.label}</SectionLabel>
        <h3 className="mt-0.5 font-serif text-base font-semibold text-foreground">{SPLIT_RECORD.heading}</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          {SPLIT_RECORD.proposalLine(SPLIT.proposedByAgent, SPLIT.agentRuns, SPLIT.agentGate)}
        </p>
        <h4 className="mt-3 text-sm font-semibold text-foreground">{SPLIT_RECORD.fieldsHeading}</h4>
        <Table className="mt-1.5">
          <TableHeader>
            <TableRow>
              <TableHead>{SPLIT_RECORD.columns.meaning}</TableHead>
              <TableHead>{SPLIT_RECORD.columns.target}</TableHead>
              <TableHead>{SPLIT_RECORD.columns.when}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {PROPOSED_FIELDS.map((field) => (
              <TableRow key={field.key}>
                <TableCell className="text-[13px] font-medium text-foreground">{field.meaning}</TableCell>
                <TableCell>
                  <span className="flex flex-col leading-tight">
                    <span className="font-mono text-[13px] text-foreground">{field.model}</span>
                    <span className="font-mono text-xs text-muted-foreground">{field.field}</span>
                  </span>
                </TableCell>
                <TableCell className="whitespace-normal text-[13px] text-ink-soft">{field.when}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <h4 className="mt-4 text-sm font-semibold text-foreground">{SPLIT_RECORD.decisionHeading}</h4>
        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          <StatusPill tone="review">Needs a person</StatusPill>
          <span className="text-sm text-ink-soft">{SPLIT_RECORD.pendingLine}</span>
        </div>
      </div>
    </div>
  )
}

/**
 * 3.6 Migrate — the plan, its rehearsal and live runs, reconciliation,
 * go/no-go and rollback state (PX-W7.5). A `section` HYBRID (Agon's
 * 2026-09-18 ruling): six LIVE panels, in order plan → runs →
 * reconciliation → go/no-go → rollback → trail, plus ONE badged concept
 * section holding the column-level field-mapping table — no backend exists
 * for it (registry `retireWhen`). No module under `features/migration/`
 * imports `@/demo/*`; this page module is the one place that does, for the
 * badged section only (see `FieldMappingConceptSection` above).
 *
 * Chrome (`moduleLabel` / `breadcrumb` / `contextPill` / `nav`) is built from
 * route params, i18n keys and this page's own live `selectProjectDetail`
 * read ONLY — never a fixture value (fix-back #2's taint pass; memory
 * `px-hybrid-route-chrome-leaks-the-fixture`).
 */
export default function MigrationPage() {
  const { t } = useTranslation('projectWorkspace')
  const { projectId } = useParams()
  const dispatch = useAppDispatch()
  const orgId = useAppSelector(selectActiveOrgId)
  const project = useAppSelector(selectProjectDetail)
  const blocked = useAppSelector(selectMigrationBlocked)
  const selectedPlanId = useAppSelector(selectSelectedPlanId)
  const selectedRunId = useAppSelector(selectSelectedRunId)

  useEffect(() => {
    if (orgId === null || projectId === undefined) return
    void dispatch(openProject({ orgId, projectId }))
    void dispatch(openMigration({ orgId, projectId }))
    return () => {
      dispatch(closeProject())
      dispatch(migrationClosed())
    }
  }, [dispatch, orgId, projectId])

  const nav = useMemo(
    () => projectNav({ id: projectId ?? '', client: project?.client ?? project?.name ?? (projectId ?? '') }),
    [projectId, project?.client, project?.name]
  )

  if (orgId === null || projectId === undefined) return null

  if (blocked !== null) {
    return (
      <AppShell
        moduleLabel={t('migration.moduleLabel')}
        breadcrumb={project?.name ?? projectId}
        contextPill={t('migration.contextPill')}
        nav={nav}
      >
        <ProjectBlockedState blocked={{ kind: 'blocked', status: blocked.status, code: blocked.code, message: blocked.message }} />
      </AppShell>
    )
  }

  return (
    <AppShell
      moduleLabel={t('migration.moduleLabel')}
      breadcrumb={project?.name ?? projectId}
      contextPill={t('migration.contextPill')}
      nav={nav}
    >
      <header className="max-w-4xl">
        <h1 className="font-serif text-2xl font-semibold text-foreground">{t('migration.pageTitle')}</h1>
        <p className="mt-1.5 text-sm text-ink-soft">{t('migration.pageStandfirst')}</p>
      </header>

      <div className="mt-5 flex flex-col gap-4">
        <MigrationPlansPanel orgId={orgId} projectId={projectId} />
        <MigrationRunsPanel orgId={orgId} projectId={projectId} planId={selectedPlanId} />
        <ReconciliationPanel orgId={orgId} projectId={projectId} planId={selectedPlanId} runId={selectedRunId} />
        <GoNoGoPanel orgId={orgId} projectId={projectId} planId={selectedPlanId} />
        <RollbackPanel orgId={orgId} projectId={projectId} planId={selectedPlanId} runId={selectedRunId} />
        <MigrationTrailPanel orgId={orgId} projectId={projectId} planId={selectedPlanId} />

        <section
          aria-label={t('migration.demoBand.title')}
          data-testid="demo-band"
          className="rounded-lg border border-border bg-card px-4 py-4"
        >
          <div className="flex flex-wrap items-center gap-2">
            <SectionLabel>{t('migration.demoBand.title')}</SectionLabel>
            <ConceptBadge />
          </div>
          <FieldMappingConceptSection />
        </section>
      </div>
    </AppShell>
  )
}
